import { db } from "./db";
import { liveQuery } from "dexie";
import { Timestamp, isHotLoad } from "../utils/utils";
import { SETTINGS } from "../services/globalVars";



// https://dexie.org/

const settings_template = {
   background: 'default',
   voice: 'unset',
   speech_rate: 1.4,
   mute: false,
   last_message: '',
   cast_voice: '',
   cast_speech_rate: 1.4,
   cast_border_size: 1,
}

const DBHandler = () => {

   const get_primary_id = () => {
      if (isHotLoad()) {
         return 10;
      } else {
         return 1;
      }
   }

   const hot_match_data_report = () => {
      return new Promise(async (resolve) => {
         try {
            const club_result = await db.club.get(10);
            const account_result = await db.account.get(10);
            const settings_result = await db.settings.get(10);
            const app_state_result = await db.app_state.get(10);
            const sync_result = await db.sync.get(10);

            resolve({
               app_state: typeof app_state_result === 'undefined' ? false : true,
               account: typeof account_result === 'undefined' ? false : true,
               club: typeof club_result === 'undefined' ? false : true,
               settings: typeof settings_result === 'undefined' ? false : true,
               sync: typeof sync_result === 'undefined' ? false : true,
               all_tables_exist: app_state_result !== false && account_result !== false && club_result !== false && settings_result !== false && sync_result !== false && sync_result !== false,
               court_id: sync_result?.ccfg_id,
            });
         } catch (error) {
            console.error('DBHandler - hot_match_data_report: ', error);
            resolve({ error: error });
         }
      });
   }

   const init = () => {
      return new Promise(async (resolve) => {
         try {
            await load_app_state();
            resolve(true);
         } catch (error) {
            console.error('DBHandler - init: ', error);
            resolve(false);
         }
      });
   }
 
   const load_app_state = () => { // TAG DONE - HOT LOAD
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            // console.log('DEBUG - load_app_state - id: ', id);
            const load_by_id = await db.app_state.get(id);
            const loaded = typeof load_by_id === 'undefined' ? await db.app_state.orderBy(':id').first() : load_by_id;

            // if (typeof loaded === 'undefined') {
            if (typeof load_by_id === 'undefined') {
               const result = await db.app_state.add({
                  id: id,
                  app_state: 'init',
                  game_state: null,
                  update: Timestamp(),
                  settings: settings_template,
               });
               console.log('DEBUG - load_app_state - created result: ', result);

               // await db.app_state.add({
               //    app_state: 'init',
               //    game_state: null,
               //    update: Timestamp(),
               //    settings: settings_template,
               // });
            }
            resolve(loaded);
         } catch (error) {
            console.warn('DBHandler - load_app_state: ', error); // This is expected to happen on hot load
            resolve(false);
         }
      });
   }

   const delete_app_state = (id = null) => {
      return new Promise(async (resolve) => {
         try {
            // console.log('>>> delete_app_state ---  id: ', id, ' <<<');
            if (id !== null) {
               await db.app_state.delete(id);
               resolve(true);
            } else {
               const loaded = await load_app_state();
               if (typeof loaded !== 'undefined') {
                  await db.app_state.delete(loaded.id);
                  resolve(true);
               } else {
                  resolve(true);
               }
            }
         } catch (error) {
            console.error('DBHandler - delete_app_state: ', error);
            resolve(false);
         }
      });
   }

   const removeParentFields = (obj) => {
      if (typeof obj !== "object" || obj === null) return obj; // Base case

      if (Array.isArray(obj)) {
         return obj.map(removeParentFields); // Recursively process arrays
      }

      let newObj = {};
      for (let key in obj) {
         if (key !== "parent") {
            newObj[key] = removeParentFields(obj[key]); // Recursively process objects
         }
      }
      return newObj;
   }

   const update_app_state = (args, caller = 'Not Set') => {
      return new Promise(async (resolve) => {
         try {
            const cleanObj = JSON.parse(JSON.stringify(removeParentFields(args)));
            const app_state = await load_app_state();
            // console.log('Update ID: ', app_state.id);
            // console.log('update_app_state: ', cleanObj);

            await db.app_state.update(app_state.id, cleanObj);
            resolve(true);
         } catch (error) {
            console.error('DBHandler - update_app_state: ', error, args);
            resolve(false);
         }
      });
   }

   const watch_app_state = (callback) => {
      const id = get_primary_id();
      const appStateObservable = liveQuery(
         () => db.app_state.get(id)
         // () => db.app_state.orderBy(':id').first()
      );
      appStateObservable.subscribe({
         next: result => callback(result),
         error: error => console.error(error)
      });
   }

   const initMatch = (matchData) => {
      return new Promise(async (resolve) => {
         try {
            const match = JSON.parse(JSON.stringify(matchData));
            delete match.id;
            // Use a transaction to ensure data is immediately available for subsequent reads
            const result = await db.transaction('rw', db.matches, async (tx) => {
               return await tx.matches.add(match);
            });
            resolve(result);
         } catch (error) {
            console.error('DBHandler - initMatch: ', error);
            resolve(false);
         }
      });
   }

   const loadAllMatches = (filter_query = null) => {
      return new Promise(async (resolve) => {
         try {
            let matchList = [];

            // Use transaction to ensure fresh reads, similar to loadMatch
            const result = await db.transaction('r', db.matches, async (tx) => {
               if (filter_query !== null) {
                  return await tx.matches.filter(filter_query).reverse().toArray();
               } else if (isHotLoad()) {
                  return await tx.matches
                  .filter(m => m.hot_load === true)
                  .reverse()
                  .toArray();
               } else {
                  return await tx.matches
                  .filter(m => m.hot_load === false)
                  .reverse()
                  .toArray();
               }
            });

            matchList = result || [];
            resolve(matchList);
         } catch (error) {
            console.error('DBHandler - loadAllMatches: ', error);
            resolve(false);
         }
      });
   }

   const loadMatch = (match_id) => {
      return new Promise(async (resolve) => {
         try {
            // Strategy to force fresh reads and bypass IndexedDB's relaxed durability caching
            // Multiple approaches to ensure we get the most up-to-date data
            
            let result = null;
            
            // Approach 1: Use a new transaction context to force fresh read
            try {
               result = await db.transaction('r', db.matches, async (tx) => {
                  return await tx.matches.get(match_id);
               });
            } catch (txError) {
               console.warn('DBHandler - loadMatch transaction failed, trying direct access:', txError);
            }

            // Approach 2: If transaction failed or returned null, try direct access
            if (!result) {
               try {
                  result = await db.matches.get(match_id);
               } catch (directError) {
                  console.warn('DBHandler - loadMatch direct access failed:', directError);
               }
            }

            // Approach 3: Force a complete refresh by waiting a brief moment and trying again
            // This helps with timing issues related to IndexedDB's asynchronous nature
            if (!result) {
               await new Promise(resolve => setTimeout(resolve, 10)); // Brief wait
               try {
                  result = await db.matches.get(match_id);
               } catch (retryError) {
                  console.warn('DBHandler - loadMatch retry failed:', retryError);
               }
            }

            // console.error('********************** .  loadMatch:', JSON.parse(JSON.stringify( result )));

            resolve(result || false);
         } catch (error) {
            console.error('DBHandler - loadMatch: ', error);
            resolve(false);
         }
      });
   }


   // const update_match_lock = {};
   const updateMatch = (id, match, tag = 'Not Set') => {
      return new Promise(async (resolve) => {
         try {
            const clean_match = JSON.parse(JSON.stringify(match));
            // console.log('DEBUG - updateMatch - clean_match: ', clean_match);
            // Use a transaction to ensure data consistency and immediate availability
            // This prevents stale data issues when reading immediately after writes
            const result = await db.transaction('rw', db.matches, async (tx) => {
               return await tx.matches.update(id, clean_match);
            });
            
            // Brief delay to ensure data is fully persisted before returning
            // This helps with IndexedDB's relaxed durability mode
            await new Promise(resolve => setTimeout(resolve, 5));
            
            resolve(result);
         } catch (error) {
            console.error('DBHandler - updateMatch: ', error);
            resolve(false);
         }
      });
   }

   const deleteMatch = (match_id) => {
      return new Promise(async (resolve) => {
         try {
            // Use a transaction to ensure deletion is immediately persisted
            await db.transaction('rw', db.matches, async (tx) => {
               await tx.matches.delete(match_id);
            });
            resolve(true);
         } catch (error) {
            console.error('DBHandler - deleteMatch: ', error);
            resolve(false);
         }
      });
   }

   const saveClock = (clock_id, clock) => {
      return new Promise(async (resolve) => {
         try {
            const cleanClock = JSON.parse(JSON.stringify(clock));
            const loadResult = await loadClock(clock_id);
            if (typeof loadResult === 'undefined') {
               const result = await db.clocks.add({
                  clock_id: clock_id,
                  clock: cleanClock
               });
               resolve(result);
            }
            else {
               const result = await db.clocks.update(clock_id, { clock: cleanClock });
               resolve(result);
            }
            resolve(false);
         } catch (error) {
            console.error('DBHandler - saveClock: ', error);
            resolve(false);
         }
      });
   }

   const loadClock = (clock_id) => {
      return new Promise(async (resolve) => {
         try {
            const result = await db.clocks.get(clock_id);
            resolve(result);
         } catch (error) {
            console.error('DBHandler - loadClock: ', error);
            resolve(false);
         }
      });
   }

   const loadSettings = () => {
      return new Promise(async (resolve) => {
         const appState = await load_app_state();
         if (typeof appState !== 'undefined') {
            resolve(appState.settings);
         }
         resolve(settings_template);
      });
   }

   const saveSettings = (args) => {
      return new Promise(async (resolve) => {
         const appState = await load_app_state();
         const settings = appState.settings;

         if (typeof args.mute !== 'undefined') {
            settings.mute = args.mute;
            SETTINGS.mute = args.mute;
         }
         if (typeof args.voice !== 'undefined') {
            settings.voice = args.voice;
            SETTINGS.voice = args.voice;
         }
         if (typeof args.background !== 'undefined') {
            settings.background = args.background;
            SETTINGS.background = args.background;
         }
         if (typeof args.speech_rate !== 'undefined') {
            settings.speech_rate = args.speech_rate;
            SETTINGS.speech_rate = args.speech_rate;
         }
         if (typeof args.last_message !== 'undefined') {
            settings.last_message = args.last_message;
            SETTINGS.last_message = args.last_message;
         }
         if (typeof args.cast_voice !== 'undefined') {
            settings.cast_voice = args.cast_voice;
            SETTINGS.cast_voice = args.cast_voice;
         }
         if (typeof args.cast_speech_rate !== 'undefined') {
            settings.cast_speech_rate = args.cast_speech_rate;
            SETTINGS.cast_speech_rate = args.cast_speech_rate;
         }
         if (typeof args.cast_border_size !== 'undefined') {
            settings.cast_border_size = args.cast_border_size;
            SETTINGS.cast_border_size = args.cast_border_size;
         }

         await update_app_state({ settings: appState.settings });

         resolve(true);
      });
   }

   const load_account = () => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await db.account.get(id);
            // const loaded = await db.account.orderBy(':id').first();
            resolve(loaded);
         } catch (error) {
            console.error('DBHandler - load_account: ', error);
            resolve(false);
         }
      });
   }

   const update_account = (data, retry = false) => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await load_account();
            if (typeof loaded === 'undefined') {
               await db.account.add({
                  id: id,
                  user_id: data.user_id,
                  username: data.username,
                  user_img: data.user_img,
                  timestamp: Timestamp(),
               });
            } else {
               if (loaded.user_id === data.user_id) {
                  await db.account.update(id, {
                     user_img: data.user_img,
                     username: data.username,
                     timestamp: Timestamp(),
                  });
               } else if (retry) {
                  console.error('Unexpected Error update_account, Dev needs to check');
                  resolve(false);
               } else {
                  db.account.delete(loaded.id);
                  await update_account(data, true);
               }
            }
            resolve(true);
         } catch (error) {
            console.error('DBHandler - update_account: ', error);
            resolve(false);
         }
      });
   }

   const delete_account = (id = null) => {
      return new Promise(async (resolve) => {
         try {
            if (id !== null) {
               await db.account.delete(id);
               resolve(true);
            } else {
               console.log('>>> delete_account <<<');
               const loaded = await load_account();
               if (typeof loaded !== 'undefined') {
                  console.log('loaded: ', loaded);
                  await db.account.delete(loaded.id);
                  resolve(true);
               } else {
                  resolve(true);
               }
            }
         } catch (error) {
            console.error('DBHandler - delete_account: ', error);
            resolve(false);
         }
      });
   }

   const load_club = () => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await db.club.get(id);

            // const loaded = await db.club.orderBy(':id').first();
            resolve(loaded);
         } catch (error) {
            console.error('DBHandler - load_club: ', error);
            resolve(false);
         }
      });
   }

   const update_club = (data, retry = false) => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await load_club();
            if (typeof loaded === 'undefined') {
               await db.club.add({
                  id: id,
                  club_id: data.club_id,
                  club_name: data.club_name,
                  club_logo: data.club_logo,
                  ad_1: data.ad_1,
                  ad_2: data.ad_2,
                  ad_3: data.ad_3,
                  timestamp: Timestamp(),
               });
            } else {
               const new_data = { ...loaded, ...JSON.parse(JSON.stringify(data)) };
               if (loaded.club_id === new_data.club_id) {
                  // for (const key in loaded) {
                  //    if (Object.hasOwnProperty.call(loaded, key)) {
                  //       if (typeof data[key] !== 'undefined') {
                  //          loaded[key] = data[key];
                  //       }
                  //    }
                  // }
                  // loaded.timestamp = Timestamp();
                  new_data.timestamp = Timestamp();
                  // console.log('Final: ', JSON.parse(JSON.stringify( new_data )));
                  await db.club.update(id, new_data);
               } else if (retry) {
                  console.error('Unexpected Error update_club, Dev needs to check');
                  resolve(false);
               } else if (retry === false) {
                  db.club.delete(id);
                  await update_club(data, true);
               }
            }
            resolve(true);
         } catch (error) {
            console.error('DBHandler - update_club retrying: ', error);
            if (retry === false) {
               await update_club(data, true);
            } else if (retry) {
               console.error('DBHandler - update_club: ', error);
               resolve(false);
            }
         }
      });
   }

   const delete_club = (id = null) => {
      return new Promise(async (resolve) => {
         try {
            // console.log('>>> delete_club -- id: ', id, ' <<<');
            if (id !== null) {
               await db.club.delete(id);
               resolve(true);
            } else {
               const loaded = await load_club();
               if (typeof loaded !== 'undefined') {
                  console.log('loaded: ', loaded);
                  await db.club.delete(loaded.id);
                  resolve(true);
               } else {
                  resolve(true);
               }
            }
         } catch (error) {
            console.error('DBHandler - delete_club: ', error);
            resolve(false);
         }
      });
   }

   const load_sync = () => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await db.sync.get(id);
            // const loaded = await db.sync.orderBy(':id').first();
            resolve(loaded);
         } catch (error) {
            console.error('DBHandler - load_sync: ', error);
            resolve(false);
         }
      });
   }
   const update_sync = (data, retry = false) => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await load_sync();
            if (typeof loaded === 'undefined') {
               await db.sync.add({
                  id: id,
                  ccfg_id: data.ccfg_id,
                  device_id: data.device_id,
                  device_name: data.device_name,
                  court_name: data.court_name,
                  court_image: data.court_image,
                  sub_courts: data.sub_courts,
                  sync_limit: data.sync_limit,
                  timestamp: Timestamp(),
               });
            } else {
               if (parseInt(loaded.device_id) === parseInt(data.device_id)) {
                  for (const key in loaded) {
                     if (Object.hasOwnProperty.call(loaded, key)) {
                        if (typeof data[key] !== 'undefined') {
                           loaded[key] = data[key];
                        }
                     }
                  }
                  loaded.timestamp = Timestamp();
                  await db.sync.update(id, loaded);
                  // await db.sync.update(loaded.device_id, loaded);
               } else if (retry) {
                  console.error('Unexpected Error update_sync, Dev needs to check');
                  resolve(false);
               } else {
                  db.sync.delete(id);
                  await update_sync(data, true);
               }
            }
            resolve(true);
         } catch (error) {
            console.error('DBHandler - update_sync: ', error);
            resolve(false);
         }
      });
   }

   const delete_sync = (id = null) => {
      return new Promise(async (resolve) => {
         try {
            // console.log('>>> delete_sync -- id: ', id, ' <<<');
            if (id !== null) {
               await db.sync.delete(id);
               resolve(true);
            } else {
               const loaded = await load_sync();
   
               if (typeof loaded !== 'undefined') {
                  console.log('loaded: ', loaded);
                  await db.sync.delete(loaded.id);
                  resolve(true);
               } else {
                  resolve(true);
               }
            }
         } catch (error) {
            console.error('DBHandler - delete_sync: ', error);
            resolve(false);
         }
      });
   }

   const load_settings = () => {
      return new Promise(async (resolve) => {
         try {
            const id = get_primary_id();
            const loaded = await db.settings.get(id);
            // const loaded = await db.settings.orderBy(':id').first();
            resolve(loaded);
         } catch (error) {
            console.error('DBHandler - settings: ', error);
            resolve(false);
         }
      });
   }

   const update_settings = (data, retry = false) => {
      return new Promise(async (resolve) => {
         try {
            const loaded = await load_settings();
            if (typeof loaded === 'undefined') {
               const id = get_primary_id();

               // console.log('update_settings - data: ', data);
               // console.log('update_settings - id: ', id);

               await db.settings.add({
                  setting_id: id,
                  id: id,
                  sport: data.sport,
                  free_app: false,
                  timestamp: Timestamp(),
               });
            } else {
               // console.log('data.setting_id: ', data.setting_id)
               // console.log('loaded.setting_id: ', loaded.setting_id)
               const new_settings = { ...loaded, ...data };
               new_settings.timestamp = Timestamp();
               // console.log('new_settings: ', new_settings);
               // console.log('new_settings.sport: ', new_settings.sport);
               await db.settings.update(loaded.setting_id, new_settings);
               // if (parseInt(loaded.setting_id) === parseInt(data.setting_id)) {
               //    for (const key in loaded) {
               //       if (Object.hasOwnProperty.call(loaded, key)) {
               //          if (typeof data[key] !== 'undefined') {
               //             loaded[key] = data[key];
               //          }
               //       }
               //    }
               //    loaded.timestamp = Timestamp();
               //    await db.settings.update(loaded.setting_id, loaded);
               // } else if (retry) {
               //    console.error('Unexpected Error update_settings, Dev needs to check');
               //    resolve(false);
               // } else {
               //    db.sync.delete(loaded.setting_id);
               //    await update_settings(data, true);
               // }
            }
            resolve(true);
         } catch (error) {
            console.error('ERROR - DBHandler - update_settings: ', error);
            resolve(false);
         }
      });
   }

   const delete_settings = () => {
      return new Promise(async (resolve) => {
         try {
            // console.log('>>> delete_settings <<<');
            const loaded = await load_sync();
            if (typeof loaded !== 'undefined') {
               console.log('loaded: ', loaded);
               await db.settings.delete(loaded.setting_id);
               resolve(true);
            } else {
               resolve(true);
            }
         } catch (error) {
            console.error('DBHandler - delete_settings: ', error);
            resolve(false);
         }
      });
   }

   const reset_database = () => {
      return new Promise((resolve) => {
         db.delete().then(() => {
         }).catch((err) => {
            console.error("ERROR - reset_database - Could not delete database: ", err);
            resolve(false);
         }).finally(() => {
            resolve(true);
         });
      });
   }

   const reset_non_critical_table = () => {
      return new Promise(async (resolve) => {
         try {
            await db.app_state.clear();
            await db.matches.clear();
            await db.clocks.clear();
            resolve(true);
         } catch (error) {
            console.error("ERROR - reset_non_critical_table - Unexpected Error: ", error);
            resolve(false);
         }
      });
   }

   return {
      init,
      initMatch,
      watch_app_state,
      update_app_state,
      delete_app_state,
      loadMatch,
      updateMatch,
      loadAllMatches,
      deleteMatch,
      saveClock,
      loadClock,
      loadSettings,
      saveSettings,
      reset_database,
      update_account,
      delete_account,
      update_club,
      delete_club,
      update_sync,
      delete_sync,
      update_settings,
      delete_settings,
      reset_non_critical_table,
      load_app_state,
      load_account,
      load_club,
      load_sync,
      load_settings,
      hot_match_data_report,
   }
}

export default DBHandler;